import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import type { TooltipProps } from 'antd/es/tooltip';

/** Above floating dashboard section rail; below opportunity assistant drawer. */
const EDITOR_ICON_TOOLTIP_Z = 10058;

const getEditorTooltipPopupContainer = () => document.body;

export type EditorChromeZone = 'sidebar' | 'actionbar' | 'toolbar';

const PLACEMENT_BY_ZONE: Record<EditorChromeZone, TooltipPlacement> = {
    sidebar: 'right',
    actionbar: 'bottom',
    toolbar: 'top',
};

export interface EditorIconTooltipProps {
    title: React.ReactNode;
    zone?: EditorChromeZone;
    placement?: TooltipPlacement;
    children: React.ReactElement;
}

function prefersClickTooltipTrigger(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

function isInsideEditorIconTooltipOverlay(target: Node): boolean {
    const overlays = document.querySelectorAll('.ant-tooltip.editor-icon-tooltip');
    for (let i = 0; i < overlays.length; i += 1) {
        if (overlays[i]!.contains(target)) return true;
    }
    return false;
}

/** White tooltip for editor menubar / toolbar icon controls. */
export const EditorIconTooltip: React.FC<EditorIconTooltipProps> = ({
    title,
    zone,
    placement,
    children,
}) => {
    const resolvedPlacement = placement ?? (zone ? PLACEMENT_BY_ZONE[zone] : 'top');
    const triggerRef = useRef<HTMLSpanElement>(null);
    const [open, setOpen] = useState(false);
    const [useClickTrigger, setUseClickTrigger] = useState(prefersClickTooltipTrigger);

    useEffect(() => {
        const media = window.matchMedia('(hover: none), (pointer: coarse)');
        const syncTrigger = () => setUseClickTrigger(media.matches);
        syncTrigger();
        media.addEventListener('change', syncTrigger);
        return () => media.removeEventListener('change', syncTrigger);
    }, []);

    const onOpenChange = useCallback((next: boolean) => {
        setOpen(next);
    }, []);

    useEffect(() => {
        if (!open) return undefined;

        const dismiss = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (triggerRef.current?.contains(target)) return;
            if (isInsideEditorIconTooltipOverlay(target)) return;
            setOpen(false);
        };

        const timerId = window.setTimeout(() => {
            document.addEventListener('pointerdown', dismiss, true);
        }, 0);

        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener('pointerdown', dismiss, true);
        };
    }, [open]);

    const trigger: TooltipProps['trigger'] = useClickTrigger ? ['click'] : ['hover'];

    return (
        <span ref={triggerRef} className="editor-icon-tooltip__trigger">
            <Tooltip
                title={title}
                placement={resolvedPlacement}
                trigger={trigger}
                open={open}
                onOpenChange={onOpenChange}
                mouseEnterDelay={0.35}
                destroyOnHidden
                zIndex={EDITOR_ICON_TOOLTIP_Z}
                getPopupContainer={getEditorTooltipPopupContainer}
                classNames={{ root: 'editor-icon-tooltip' }}
            >
                {children}
            </Tooltip>
        </span>
    );
};
